// chore: re-sync nudge for Lovable GitHub bridge (gads Phase 2, PRs #180/#181)
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { WalkUploadProvider } from "@/contexts/WalkUploadContext";
import { WalkUploadIndicator } from "@/components/WalkUploadIndicator";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useMetaPageView } from "@/hooks/useMetaPageView";
import { useContentsquare } from "@/hooks/useContentsquare";
import { useAppOpenTracking, usePageTracking, useSessionTracking } from "@/hooks/useActivityTracker";
import { useUtmCapture } from "@/hooks/useUtmCapture";
import { initWebVitals } from "@/lib/webVitals";
import { CookieConsent } from "@/components/CookieConsent";
import { NeueVersionHinweis } from "@/components/NeueVersionHinweis";
import { ClarityLoader } from "@/components/ClarityLoader";
import { RouteGuard } from "@/components/admin/RouteGuard";
import { NativeRouteGuard } from "@/components/NativeRouteGuard";
// Hinweise für den ganzen Kundenbereich (Impfpass-Erneuerung 1.41): im Einstieg steht nur die
// Pfad-Prüfung, der Wächter (Login, Leerlauf) und die Leiste sind eigene Chunks und laden nur auf
// Kundenrouten. So bleibt die Startkette von /dashboard schlank (Bundle-Budget).
import { istKundenPfad } from "@/lib/kundenPfade";
import { isNative } from "@/lib/platform";
import { appStatistikVerfuegbar } from "@/lib/appStatistikStand";
import { teamRechteSetzen } from "@/lib/teamModus";
import { appSperreNutzer } from "@/lib/biometrieMerker";
// Lazy load all pages and components for code splitting
const Index = lazy(() => import("./pages/Index"));
const ChatBotWrapper = lazy(() => import("./components/ChatBotWrapper").then(m => ({ default: m.ChatBotWrapper })));
const Services = lazy(() => import("./pages/Services"));
const Preise = lazy(() => import("./pages/Preise"));
const About = lazy(() => import("./pages/About"));
const Booking = lazy(() => import("./pages/Booking"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthActivate = lazy(() => import("./pages/AuthActivate"));
// App-Onboarding B3 (02.11.2026): Store-Links aus Mails/QR zählen je Quelle, dann Store.
const AppWeiterleitung = lazy(() => import("./pages/AppWeiterleitung"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DogProfile = lazy(() => import("./pages/DogProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Impressum = lazy(() => import("./pages/Impressum"));
const AGB = lazy(() => import("./pages/AGB"));
// § 312k-Kündigungsbutton (W0, 10.09.2026) — öffentlich, ohne Login
const VertraegeKuendigen = lazy(() => import("./pages/VertraegeKuendigen"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const AgbHundetagesbetreuung = lazy(() => import("./pages/AgbHundetagesbetreuung"));
const HutaGesundheit = lazy(() => import("./pages/HutaGesundheit"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const KontoLoeschen = lazy(() => import("./pages/KontoLoeschen"));
const Einladung = lazy(() => import("./pages/Einladung"));
// Nur in der App: Hinweis bei fehlender Internetverbindung (Launch-Lücke 05.10.2026).
const OfflineHinweis = lazy(() => import("./components/app/OfflineHinweis"));
// Nur in der App: Team-Seiten per Rolle (OA-5). Prüft Schalter `team_seiten` + Rechte und gibt
// Team-Adressen im Routen-Wächter frei. Scheitert der Chunk, gilt „kein Team“ (fail closed).
const TeamZugang = lazy(() =>
  import("./components/team/TeamZugang").catch(() => {
    teamRechteSetzen("nein");
    return { default: () => null };
  }),
);
// Nur in der App: Face ID / Touch ID (Store-Build 1.0.1): Token-Pflege der schnellen Anmeldung und
// App-Sperre. Fehlt das native Plugin (App 1.0), tut der Chunk nichts. Scheitert er, keine Sperre.
const BiometrieWaechter = lazy(() =>
  import("./components/app/BiometrieWaechter").catch(() => ({ default: () => null })),
);
// Nur in der App: Push (KA-09, K8). App-weit, damit ein angetippter Push von JEDER Seite zum Ziel
// führt, nicht nur vom Dashboard aus. Prüft Login und Schalter `push` selbst. Scheitert der Chunk,
// läuft die App ohne Push-Listener weiter.
const PushSetup = lazy(() =>
  import("./components/app/PushSetup").catch(() => ({ default: () => null })),
);
// /gassi-service: eine Seite (GassiServiceA). Der A/A-Router (gassi_hero, gestoppt 18.09.2026)
// ist seit 30.10.2026 aufgelöst, /gassi-service-b leitet auf /gassi-service weiter.
const GassiServiceA = lazy(() => import("./pages/GassiServiceA"));
const Schnuppertag = lazy(() => import("./pages/Schnuppertag"));
const SchnuppertagSuccess = lazy(() => import("./pages/SchnuppertagSuccess"));
const GassiSuccess = lazy(() => import("./pages/GassiSuccess"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const DaycareCheck = lazy(() => import("./pages/DaycareCheck"));
// Impfpass per persönlichem Link ohne Login (Folge-PR #1090), reine Web-Seite.
const ImpfpassHochladen = lazy(() => import("./pages/ImpfpassHochladen"));
const HundAbholen = lazy(() => import("./pages/HundAbholen"));
const Kundenausweis = lazy(() => import("./pages/Kundenausweis"));
// App-Design-System light (B1): interne Demo, nur Founder bzw. Dev-Server, nicht in Sitemap/App.
const AppUiDemo = lazy(() => import("./pages/intern/AppUiDemo"));
const DaycareInteresse = lazy(() => import("./pages/DaycareInteresse"));
const DaycareOnboarding = lazy(() => import("./pages/DaycareOnboarding"));
const DaycareZweithundNachtrag = lazy(() => import("./pages/DaycareZweithundNachtrag"));
const DaycareAboAbsagen = lazy(() => import("./pages/DaycareAboAbsagen"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const HundAlleineLassen = lazy(() => import("./pages/blog/HundAlleineLassen"));
const GassiServiceVsHundekita = lazy(() => import("./pages/blog/GassiServiceVsHundekita"));
const HundespaziergaengeMuenchen = lazy(() => import("./pages/blog/HundespaziergaengeMuenchen"));
const HundebetreuungSteuerlichAbsetzen = lazy(() => import("./pages/blog/HundebetreuungSteuerlichAbsetzen"));
const TrennungsangstHund = lazy(() => import("./pages/blog/TrennungsangstHund"));
const HundefreundlicheCafesMuenchen = lazy(() => import("./pages/blog/HundefreundlicheCafesMuenchen"));
const WelpeEingewoehnen = lazy(() => import("./pages/blog/WelpeEingewoehnen"));
const HundeseeMuenchen = lazy(() => import("./pages/blog/HundeseeMuenchen"));
const WandernMitHundAlpen = lazy(() => import("./pages/blog/WandernMitHundAlpen"));
const HundewieseMuenchen = lazy(() => import("./pages/blog/HundewieseMuenchen"));
const HundebetreuungKosten = lazy(() => import("./pages/blog/HundebetreuungKosten"));
const HundepensionVergleich = lazy(() => import("./pages/blog/HundepensionVergleich"));
const BerufstaetigMitHund = lazy(() => import("./pages/blog/BerufstaetigMitHund"));
const BuerohundMuenchen = lazy(() => import("./pages/blog/BuerohundMuenchen"));
const HundMvg = lazy(() => import("./pages/blog/HundMvg"));
const HundHitzeSommer = lazy(() => import("./pages/blog/HundHitzeSommer"));
const SilvesterMitHund = lazy(() => import("./pages/blog/SilvesterMitHund"));
const TierarztNotdienst = lazy(() => import("./pages/blog/TierarztNotdienst"));
const MuenchenMitHund = lazy(() => import("./pages/blog/MuenchenMitHund"));
const HundesitterFinden = lazy(() => import("./pages/blog/HundesitterFinden"));
const UrlaubOhneHund = lazy(() => import("./pages/blog/UrlaubOhneHund"));
const HundWiesnOktoberfest = lazy(() => import("./pages/blog/HundWiesnOktoberfest"));
const HundebetreuungWiesn = lazy(() => import("./pages/blog/HundebetreuungWiesn"));
const Walker = lazy(() => import("./pages/Walker"));
const Hundetagesstaette = lazy(() => import("./pages/Hundetagesstaette"));
const ServiceFinder = lazy(() => import("./pages/ServiceFinder"));
const HutaTelefonTermin = lazy(() => import("./pages/HutaTelefonTermin"));
const HundesitterMuenchen = lazy(() => import("./pages/HundesitterMuenchen"));
const GassiStadtteil = lazy(() => import("./pages/GassiStadtteil"));
const En = lazy(() => import("./pages/En"));
const Export = lazy(() => import("./pages/Export"));
const Buchen = lazy(() => import("./pages/Buchen"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobsSuccess = lazy(() => import("./pages/JobsSuccess"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminCapacity = lazy(() => import("./pages/admin/AdminCapacity"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminDogs = lazy(() => import("./pages/admin/AdminDogs"));
const AdminDogEdit = lazy(() => import("./pages/admin/AdminDogEdit"));
const AdminDogLevelReview = lazy(() => import("./pages/admin/AdminDogLevelReview"));
const AdminDaycareOverview = lazy(() => import("./pages/admin/AdminDaycareOverview"));
const AdminDaycareCapacity = lazy(() => import("./pages/admin/AdminDaycareCapacity"));
const AdminDaycareInbox = lazy(() => import("./pages/admin/AdminDaycareInbox"));
const AdminDaycareCheckin = lazy(() => import("./pages/admin/AdminDaycareCheckin"));
const AdminDaycareAbos = lazy(() => import("./pages/admin/AdminDaycareAbos"));
const AdminDaycareAboStats = lazy(() => import("./pages/admin/AdminDaycareAboStats"));
const AdminDaycareTasks = lazy(() => import("./pages/admin/AdminDaycareTasks"));
const AdminDaycareGruppen = lazy(() => import("./pages/admin/AdminDaycareGruppen"));
const AdminDaycareTodos = lazy(() => import("./pages/admin/AdminDaycareTodos"));
const AdminFreistellerPruefung = lazy(() => import("./pages/admin/AdminFreistellerPruefung"));
const AdminAvatare = lazy(() => import("./pages/admin/AdminAvatare"));
// CharliesCam CC-5 (Testmodus, hinter App-Schalter huta_kamera_bilder): eigener nachgeladener Baustein.
const AdminDaycareKamera = lazy(() => import("./pages/admin/AdminDaycareKamera"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));
const AdminPasses = lazy(() => import("./pages/admin/AdminPasses"));
const AdminSchnuppertag = lazy(() => import("./pages/admin/AdminSchnuppertag"));
const AdminGassiFunnel = lazy(() => import("./pages/admin/AdminGassiFunnel"));
const AdminRegeltermin = lazy(() => import("./pages/admin/AdminRegeltermin"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminRoutes = lazy(() => import("./pages/admin/AdminRoutes"));
const AdminChatAnalytics = lazy(() => import("./pages/admin/AdminChatAnalytics"));
const AdminCancellations = lazy(() => import("./pages/admin/AdminCancellations"));
const AdminSchnuppertagStats = lazy(() => import("./pages/admin/AdminSchnuppertagStats"));
const AdminDogRename = lazy(() => import("./pages/admin/AdminDogRename"));
const AdminCustomerStatus = lazy(() => import("./pages/admin/AdminCustomerStatus"));
const AdminSpecials = lazy(() => import("./pages/admin/AdminSpecials"));
const AdminCustomersByPLZ = lazy(() => import("./pages/admin/AdminCustomersByPLZ"));
const AdminEmployees = lazy(() => import("./pages/admin/AdminEmployees"));
const AdminWalkerApplications = lazy(() => import("./pages/admin/AdminWalkerApplications"));
const AdminTrialSlots = lazy(() => import("./pages/admin/AdminTrialSlots"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminFixUserIds = lazy(() => import("./pages/admin/AdminFixUserIds"));
const AdminKYC = lazy(() => import("./pages/admin/AdminKYC"));
const AdminBrainInput = lazy(() => import("./pages/admin/AdminBrainInput"));
const AdminAIOperator = lazy(() => import("./pages/admin/AdminAIOperator"));
const AdminDogWalkerDashboard = lazy(() => import("./pages/admin/AdminDogWalkerDashboard"));
const AdminCustomerBookings = lazy(() => import("./pages/admin/AdminCustomerBookings"));
const AdminCustomerAuditLog = lazy(() => import("./pages/admin/AdminCustomerAuditLog"));
const AdminCustomerHistory = lazy(() => import("./pages/admin/AdminCustomerHistory"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminCustomerProfile = lazy(() => import("./pages/admin/AdminCustomerProfile"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminWalkPhotoAudit = lazy(() => import("./pages/admin/AdminWalkPhotoAudit"));
const AdminWebsiteTraffic = lazy(() => import("./pages/admin/AdminWebsiteTraffic"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews"));
const AdminSyncDashboard = lazy(() => import("./pages/admin/AdminSyncDashboard"));
const AdminCustomerSupport = lazy(() => import("./pages/admin/AdminCustomerSupport"));
const AdminShiftAssignment = lazy(() => import("./pages/admin/AdminShiftAssignment"));
const AdminPersonenRechte = lazy(() => import("./pages/admin/AdminPersonenRechte"));
const AdminLocations = lazy(() => import("./pages/admin/AdminLocations"));
const AdminKontrollzentrum = lazy(() => import("./pages/admin/AdminKontrollzentrum"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,    // 5 minutes
      retry: 1,                      // 1 retry instead of default 3
      refetchOnWindowFocus: false,   // Don't refetch on every tab switch
    },
  },
});

// Loading fallback component. data-seitenlader: der Web-Tracker (src/lib/interactions.ts) misst
// keine Scrolltiefe, solange dieser bildschirmhohe Platzhalter statt des Routeninhalts steht.
const PageLoader = () => (
  <div data-seitenlader="" className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Component to track route changes and analytics
const AnalyticsTracker = () => {
  useGoogleAnalytics('G-9CFJBN08VK');
  useContentsquare();
  usePageTracking();
  useSessionTracking();
  useMetaPageView();
  useUtmCapture();
  // Real-User Core Web Vitals: EINMAL beim App-Start registrieren (client-only).
  // Fire-and-forget + try/catch intern → kann die Seite nie brechen.
  useEffect(() => {
    initWebVitals();
    // LP-Engagement (Scroll/Sektionen/Klicks) — gleiche Garantien.
    import("@/lib/interactions").then((m) => m.initInteractions()).catch(() => {});
  }, []);
  return null;
};

// In der nativen App (Spec v3 §3.3): nur eigenes First-Party-Tracking, keine Fremdanbieter
// (Meta, Google, Contentsquare, Clarity). Sonst verlangt Apple den ATT-Dialog, und der
// Kundenbereich braucht diese Skripte nicht. Kein Website-Chatbot, kein Cookie-Banner.
const IS_NATIVE = isNative();
// App-Sperre an (Merker, synchron): bis der Wächter-Chunk steht, deckt eine Creme-Fläche die Seite ab.
const SPERRE_BEIM_START = IS_NATIVE && appSperreNutzer() !== null;

const NativeAnalyticsTracker = () => {
  usePageTracking();
  useSessionTracking();
  useAppOpenTracking();
  useEffect(() => {
    initWebVitals();
    // Firebase Analytics (Noah 08.10.2026): gespeicherte Einwilligung „Statistik“ auf das native
    // Plugin übertragen (ohne Einwilligung bleibt es aus). Eigener kleiner Chunk, nur in der App.
    // Ältere Store-Builds ohne Firebase laden den Chunk gar nicht erst.
    if (appStatistikVerfuegbar()) import("@/lib/appStatistik").then((m) => m.appStatistikAnwenden()).catch(() => {});
  }, []);
  return null;
};

const KundenbereichHinweise = lazy(() => import("@/components/impfpass/KundenbereichHinweise"));
/** Lädt die Kundenbereich-Hinweise nur auf Kundenrouten (sonst kein Request, nichts im Einstieg). */
function KundenbereichHinweiseTor() {
  const { pathname } = useLocation();
  if (!istKundenPfad(pathname)) return null;
  return (
    <Suspense fallback={null}>
      <KundenbereichHinweise />
    </Suspense>
  );
}

/** Dauerhafte Weiterleitung einer eingestellten Adresse; Query und Hash wandern mit. */
function WeiterleitungMitQuery({ nach }: { nach: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: nach, search, hash }} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <WalkUploadProvider>
        {/* Kein globaler TooltipProvider mehr (14.10.2026, Bundle-Budget): jeder Tooltip bringt
            seinen eigenen mit, siehe components/ui/tooltip.tsx. */}
        <Toaster />
        <Sonner />
        <WalkUploadIndicator />
        <BrowserRouter>
          <ScrollToTop />
          {IS_NATIVE ? <NativeAnalyticsTracker /> : <AnalyticsTracker />}
          <NativeRouteGuard>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/preise" element={<Preise />} />
              {/* /pricing war tot (Alt-Tabelle pricing); seit 15.09.2026 → /preise */}
              <Route path="/pricing" element={<Navigate to="/preise" replace />} />
              <Route path="/about" element={<About />} />

              <Route path="/booking" element={<Booking />} />
              <Route path="/booking/success" element={<BookingSuccess />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/activate" element={<AuthActivate />} />
              <Route path="/app" element={<AppWeiterleitung />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/login" element={<Auth />} />
              {/* /dashboard/* = Kundenbereich v2 (K1): Tabs als eigene Adressen. EINE Route, damit der
                  Tabwechsel das Dashboard nicht neu mountet. Ohne Schalter leitet das Dashboard
                  /dashboard/<tab> selbst auf die alten Adressen um. */}
              <Route path="/dashboard/*" element={<Dashboard />} />
              <Route path="/dog/:dogId" element={<DogProfile />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/agb" element={<AGB />} />
              <Route path="/vertraege-kuendigen" element={<VertraegeKuendigen />} />
              <Route path="/widerruf" element={<Widerruf />} />
              {/* Bewusst nicht in Nav/Sitemap (L13, 13.09.2026): nur aus Onboarding, Bestellschritt, Mails erreichbar. */}
              <Route path="/agb-hundetagesbetreuung" element={<AgbHundetagesbetreuung />} />
              <Route path="/huta-gesundheit" element={<HutaGesundheit />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/konto-loeschen" element={<KontoLoeschen />} />
              {/* Haushalt (KA-29): Einladungslink, nicht in Sitemap. */}
              <Route path="/einladung/:token" element={<Einladung />} />
              {/* Produkt eingestellt — alte Links/Index-Einträge weiterleiten */}
              <Route path="/hundereisen" element={<Navigate to="/gassi-service" replace />} />
              <Route path="/gassi-service" element={<GassiServiceA />} />
              {/* Alte Force-Preview-URL des A/A-Tests, Query (UTM) bleibt erhalten */}
              <Route path="/gassi-service-b" element={<WeiterleitungMitQuery nach="/gassi-service" />} />
              <Route path="/schnuppertag" element={<Schnuppertag />} />
              <Route path="/schnuppertag-success" element={<SchnuppertagSuccess />} />
              <Route path="/gassi-success" element={<GassiSuccess />} />
              <Route path="/newsletter-confirm" element={<NewsletterConfirm />} />
              <Route path="/newsletter/abmelden" element={<NewsletterUnsubscribe />} />
              <Route path="/daycare/check" element={<DaycareCheck />} />
              <Route path="/impfpass/hochladen" element={<ImpfpassHochladen />} />
              <Route path="/daycare/getemployee" element={<HundAbholen />} />
              <Route path="/daycare/abholen" element={<Navigate to="/daycare/getemployee" replace />} />
              <Route path="/kundenausweis" element={<Kundenausweis />} />
              <Route path="/intern/app-ui" element={<AppUiDemo />} />
              <Route path="/daycare/interesse" element={<DaycareInteresse />} />
              <Route path="/daycare/onboarding" element={<DaycareOnboarding />} />
              <Route path="/daycare/zweithund" element={<DaycareZweithundNachtrag />} />
              <Route path="/daycare/abo-absagen" element={<DaycareAboAbsagen />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/muenchen-mit-hund-guide" element={<MuenchenMitHund />} />
              <Route path="/blog/hundesitter-finden-muenchen" element={<HundesitterFinden />} />
              <Route path="/blog/urlaub-ohne-hund-betreuung-muenchen" element={<UrlaubOhneHund />} />
              <Route path="/blog/hund-wiesn-oktoberfest-muenchen" element={<HundWiesnOktoberfest />} />
              <Route path="/blog/hundebetreuung-wiesn-muenchen" element={<HundebetreuungWiesn />} />
              <Route path="/blog/hund-alleine-lassen-wie-lange" element={<HundAlleineLassen />} />
              <Route path="/blog/gassi-service-vs-hundekita-vergleich" element={<GassiServiceVsHundekita />} />
              <Route path="/blog/schoenste-hundespaziergaenge-muenchen" element={<HundespaziergaengeMuenchen />} />
              <Route path="/blog/hundebetreuung-steuerlich-absetzen" element={<HundebetreuungSteuerlichAbsetzen />} />
              <Route path="/blog/trennungsangst-hund-symptome-loesungen" element={<TrennungsangstHund />} />
              <Route path="/blog/hundefreundliche-cafes-biergaerten-muenchen" element={<HundefreundlicheCafesMuenchen />} />
              <Route path="/blog/welpe-eingewoehnen-erste-wochen" element={<WelpeEingewoehnen />} />
              <Route path="/blog/hundesee-muenchen-baden-mit-hund" element={<HundeseeMuenchen />} />
              <Route path="/blog/wandern-mit-hund-alpen-touren-ab-muenchen" element={<WandernMitHundAlpen />} />
              <Route path="/blog/hundewiese-muenchen-freilauf" element={<HundewieseMuenchen />} />
              <Route path="/blog/hundebetreuung-muenchen-kosten" element={<HundebetreuungKosten />} />
              <Route path="/blog/hundepension-hundesitter-hundetagesstaette-vergleich" element={<HundepensionVergleich />} />
              <Route path="/blog/berufstaetig-mit-hund-alltag" element={<BerufstaetigMitHund />} />
              <Route path="/blog/buerohund-muenchen" element={<BuerohundMuenchen />} />
              <Route path="/blog/hund-mvg-ubahn-muenchen" element={<HundMvg />} />
              <Route path="/blog/hund-hitze-sommer-tipps" element={<HundHitzeSommer />} />
              <Route path="/blog/silvester-mit-hund" element={<SilvesterMitHund />} />
              <Route path="/blog/tierarzt-notdienst-muenchen" element={<TierarztNotdienst />} />
              <Route path="/walker" element={<Walker />} />
              <Route path="/hundetagesstaette" element={<Hundetagesstaette />} />
              <Route path="/welcher-service" element={<ServiceFinder />} />
              <Route path="/hundesitter-muenchen" element={<HundesitterMuenchen />} />
              {/* Stadtteilseiten (11.10.2026), Daten in src/data/gassiStadtteile.ts */}
              <Route path="/gassi/:slug" element={<GassiStadtteil />} />
              {/* Englische Kurzseite (12.10.2026), Texte in src/data/seiteEn.ts */}
              <Route path="/en" element={<En />} />
              <Route path="/export" element={<Export />} />
              {/* Partnerseite eingestellt (Noah 13.10.2026): /benefits, Unterpfade und /benefit leiten
                  auf /gassi-service weiter, Query (UTM, ref) und Hash bleiben erhalten. */}
              <Route path="/benefits/*" element={<WeiterleitungMitQuery nach="/gassi-service" />} />
              <Route path="/benefit" element={<WeiterleitungMitQuery nach="/gassi-service" />} />
              <Route path="/huta-telefon-termin" element={<HutaTelefonTermin />} />
              <Route path="/buchen" element={<Buchen />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/success" element={<JobsSuccess />} />
              {/* Admin Routes */}
              {/* INVARIANT: the /admin parent and its index route MUST stay UNGUARDED.
                  RouteGuard redirects unauthorized users to /admin, so guarding the
                  index would create a redirect loop for any admitted user lacking
                  that key. */}
              <Route path="/admin" element={<AdminLayout />}>
                {/* MUST stay UNGUARDED — guarding the index would loop (see note above). */}
                <Route index element={<AdminOverview />} />
                
                <Route path="calendar" element={<RouteGuard><AdminCalendar /></RouteGuard>} />
                <Route path="routes" element={<RouteGuard><AdminRoutes /></RouteGuard>} />
                <Route path="capacity" element={<RouteGuard><AdminCapacity /></RouteGuard>} />
                <Route path="payments" element={<RouteGuard><AdminPayments /></RouteGuard>} />
                <Route path="customers" element={<RouteGuard><AdminCustomers /></RouteGuard>} />
                <Route path="customers/:customerId" element={<RouteGuard><AdminCustomerProfile /></RouteGuard>} />
                <Route path="customer-bookings" element={<RouteGuard><AdminCustomerBookings /></RouteGuard>} />
                <Route path="customers-plz" element={<RouteGuard><AdminCustomersByPLZ /></RouteGuard>} />
                <Route path="dogs" element={<RouteGuard><AdminDogs /></RouteGuard>} />
                <Route path="dogs/:dogId" element={<RouteGuard><AdminDogEdit /></RouteGuard>} />
                <Route path="dog-level-review" element={<RouteGuard><AdminDogLevelReview /></RouteGuard>} />
                <Route path="daycare" element={<RouteGuard><AdminDaycareOverview /></RouteGuard>} />
                <Route path="daycare/kapazitaet" element={<RouteGuard><AdminDaycareCapacity /></RouteGuard>} />
                <Route path="daycare/postfach" element={<RouteGuard><AdminDaycareInbox /></RouteGuard>} />
                <Route path="daycare/checkin" element={<RouteGuard><AdminDaycareCheckin /></RouteGuard>} />
                <Route path="daycare/abos" element={<RouteGuard><AdminDaycareAbos /></RouteGuard>} />
                <Route path="stats/daycare-abos" element={<RouteGuard><AdminDaycareAboStats /></RouteGuard>} />
                <Route path="daycare/aufgaben" element={<RouteGuard><AdminDaycareTasks /></RouteGuard>} />
                <Route path="daycare/gruppen" element={<RouteGuard><AdminDaycareGruppen /></RouteGuard>} />
                <Route path="freisteller-pruefung" element={<RouteGuard><AdminFreistellerPruefung /></RouteGuard>} />
                <Route path="avatare" element={<RouteGuard><AdminAvatare /></RouteGuard>} />
                <Route path="daycare/todos" element={<RouteGuard><AdminDaycareTodos /></RouteGuard>} />
                <Route path="daycare/kamera" element={<RouteGuard><AdminDaycareKamera /></RouteGuard>} />
                <Route path="funnel" element={<RouteGuard><AdminFunnel /></RouteGuard>} />
                {/* Stillgelegt 04.10.2026 (Noah: „kann wegbleiben“, Roadmap 1.21): alte Adressen führen auf /admin. */}
                <Route path="bookings" element={<Navigate to="/admin" replace />} />
                <Route path="adventures" element={<Navigate to="/admin" replace />} />
                <Route path="gassi-funnel" element={<RouteGuard><AdminGassiFunnel /></RouteGuard>} />
                <Route path="schnuppertag" element={<RouteGuard><AdminSchnuppertag /></RouteGuard>} />
                <Route path="regeltermin" element={<RouteGuard><AdminRegeltermin /></RouteGuard>} />
                <Route path="passes" element={<RouteGuard><AdminPasses /></RouteGuard>} />
                {/* Alte Rollenverwaltung (user_roles) seit Rollen-Cutover 02.10.2026 stillgelegt → Rollen */}
                <Route path="users" element={<Navigate to="/admin/personen-rechte?tab=personen" replace />} />
                <Route path="chat-analytics" element={<RouteGuard><AdminChatAnalytics /></RouteGuard>} />
                <Route path="ab-testing" element={<Navigate to="/admin" replace />} />
                <Route path="ab-testing-legacy" element={<Navigate to="/admin" replace />} />
                <Route path="cancellations" element={<RouteGuard><AdminCancellations /></RouteGuard>} />
                <Route path="schnuppertag-stats" element={<RouteGuard><AdminSchnuppertagStats /></RouteGuard>} />
                <Route path="dog-rename" element={<RouteGuard><AdminDogRename /></RouteGuard>} />
                <Route path="customer-status" element={<RouteGuard><AdminCustomerStatus /></RouteGuard>} />
                <Route path="specials" element={<RouteGuard><AdminSpecials /></RouteGuard>} />
                <Route path="cohort-analysis" element={<Navigate to="/admin" replace />} />
                <Route path="employees" element={<RouteGuard><AdminEmployees /></RouteGuard>} />
                <Route path="walker-applications" element={<RouteGuard><AdminWalkerApplications /></RouteGuard>} />
                <Route path="trial-slots" element={<RouteGuard><AdminTrialSlots /></RouteGuard>} />
                <Route path="retention" element={<Navigate to="/admin" replace />} />
                <Route path="activity-log" element={<RouteGuard><AdminActivityLog /></RouteGuard>} />
                <Route path="fix-user-ids" element={<RouteGuard><AdminFixUserIds /></RouteGuard>} />
                <Route path="kyc" element={<RouteGuard><AdminKYC /></RouteGuard>} />
                <Route path="brain-input" element={<RouteGuard><AdminBrainInput /></RouteGuard>} />
                <Route path="marketing/ai-operator" element={<RouteGuard><AdminAIOperator /></RouteGuard>} />
                <Route path="dogwalker" element={<AdminDogWalkerDashboard />} />
                <Route path="customer-audit-log" element={<RouteGuard><AdminCustomerAuditLog /></RouteGuard>} />
                <Route path="customer-history" element={<RouteGuard><AdminCustomerHistory /></RouteGuard>} />
                <Route path="feedback" element={<RouteGuard><AdminFeedback /></RouteGuard>} />
                <Route path="referrals" element={<RouteGuard><AdminReferrals /></RouteGuard>} />
                <Route path="walk-photo-audit" element={<RouteGuard><AdminWalkPhotoAudit /></RouteGuard>} />
                <Route path="stadtteile" element={<Navigate to="/admin" replace />} />
                <Route path="stats/website-traffic" element={<RouteGuard><AdminWebsiteTraffic /></RouteGuard>} />
                <Route path="news" element={<RouteGuard><AdminNews /></RouteGuard>} />
                <Route path="sync-dashboard" element={<RouteGuard><AdminSyncDashboard /></RouteGuard>} />
                <Route path="customer-support" element={<RouteGuard><AdminCustomerSupport /></RouteGuard>} />
                <Route path="shift-assignment" element={<RouteGuard><AdminShiftAssignment /></RouteGuard>} />
                <Route path="roles" element={<RouteGuard><Navigate to="/admin/personen-rechte?tab=personen" replace /></RouteGuard>} />
                <Route path="page-access" element={<RouteGuard><Navigate to="/admin/personen-rechte" replace /></RouteGuard>} />
                <Route path="personen-rechte" element={<RouteGuard><AdminPersonenRechte /></RouteGuard>} />
                <Route path="locations" element={<RouteGuard><AdminLocations /></RouteGuard>} />
                <Route path="kontrollzentrum" element={<RouteGuard><AdminKontrollzentrum /></RouteGuard>} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </NativeRouteGuard>
          <KundenbereichHinweiseTor />
          {!IS_NATIVE && (
            <Suspense fallback={null}>
              <ChatBotWrapper />
            </Suspense>
          )}
          {!IS_NATIVE && <CookieConsent />}
          <NeueVersionHinweis />
          {IS_NATIVE && (
            <Suspense fallback={null}>
              <OfflineHinweis />
            </Suspense>
          )}
          {IS_NATIVE && (
            <Suspense fallback={null}>
              <TeamZugang />
            </Suspense>
          )}
          {IS_NATIVE && (
            <Suspense fallback={SPERRE_BEIM_START ? <div className="fixed inset-0 z-[1000] bg-[#F3EFE8]" aria-hidden="true" /> : null}>
              <BiometrieWaechter startGesperrt={SPERRE_BEIM_START} />
            </Suspense>
          )}
          {IS_NATIVE && (
            <Suspense fallback={null}>
              <PushSetup />
            </Suspense>
          )}
          {!IS_NATIVE && <ClarityLoader />}
        </BrowserRouter>
      </WalkUploadProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;

// lovable-sync-nudge 2026-07-13-1
